import { metrics } from '@opentelemetry/api'

import { INSTRUMENTATION_NAME } from '../constants'
import { normalizePathname } from './utils/normalizePathname'

/**
 * Set up meaningful distributions to distinguish "barely over threshold" from "severely blocking",
 * rather than the default histogram boundaries optimized for HTTP latency.
 */
const LONG_TASK_BUCKET_BOUNDARIES = [50, 100, 200, 300, 400, 500, 600, 800, 1000]

const isLongTaskEntry = (entry: PerformanceEntry): entry is PerformanceLongTaskTiming => entry.entryType === 'longtask'

export const trackBrowserLongTasks = () => {
    // Only available in Chromium-based browsers
    if (!PerformanceObserver.supportedEntryTypes.includes('longtask')) return

    const meter = metrics.getMeter(INSTRUMENTATION_NAME)

    const longTaskCount = meter.createCounter('app.browser_long_tasks', {
        description: 'Number of long tasks (>50ms) blocking the main thread',
    })

    const longTaskDuration = meter.createHistogram('app.browser_long_task_duration', {
        description: 'Duration of long tasks blocking the main thread',
        advice: { explicitBucketBoundaries: LONG_TASK_BUCKET_BOUNDARIES },
        unit: 'ms',
    })

    new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
            if (!isLongTaskEntry(entry)) continue

            const metricAttributes = {
                'task.container_type': entry.attribution?.[0]?.containerType ?? 'window',
                // Classify as "initial page load" if it occurs within the first 5 seconds, otherwise 'runtime'
                'task.phase': entry.startTime < 5000 ? 'initial-load' : 'runtime',
                'di.url.path': normalizePathname(window.location.pathname),
            }

            longTaskCount.add(1, metricAttributes)
            longTaskDuration.record(entry.duration, metricAttributes)
        }
    }).observe({ entryTypes: ['longtask'] })
}
